import { z } from 'zod';
import {
    ApiUserRole,
    ApiChatAttachmentKind,
    ApiChatChannelType,
    ApiChatMessageKind,
    ApiChatMessageSendStatus,
    ApiChatReadState,
    type ChatRealtimeEventResponse,
} from '@/core/generated/api';

const sequence = z.number()
    .int()
    .min(0)
    .max(Number.MAX_SAFE_INTEGER);

const nullableText = z.string()
    .nullish();

const message = z.object({
    id: z.guid(),
    retryAttemptId: z.guid()
        .nullish(),
    sequence: sequence.min(1),
    senderParticipantId: z.guid(),
    senderName: nullableText,
    senderAvatarUrl: nullableText,
    authorParticipantId: z.guid()
        .nullish(),
    authorName: nullableText,
    authorAppUserId: z.guid()
        .nullish(),
    editVersion: sequence.optional(),
    editedAt: z.iso.datetime({ offset: true }).nullish(),
    isOutgoing: z.boolean(),
    text: nullableText,
    kind: z.object({
        id: z.enum(ApiChatMessageKind),
        name: z.string(),
    }),
    receivedAt: z.iso.datetime({ offset: true }),
    sentAt: z.iso.datetime({ offset: true }).nullish(),
    sendStatus: z.object({
        id: z.enum(ApiChatMessageSendStatus),
        name: z.string(),
    }),
    readState: z.object({
        id: z.enum(ApiChatReadState),
        name: z.string(),
    }),
    attachments: z.array(z.object({
        id: z.guid(),
        position: sequence,
        kind: z.object({
            id: z.enum(ApiChatAttachmentKind),
            name: z.string(),
        }),
        url: nullableText,
        fileName: nullableText,
        mimeType: nullableText,
        sizeBytes: sequence.nullish(),
        previewUrl: nullableText,
    })),
});

const dialog = z.object({
    id: z.guid(),
    companionId: z.guid(),
    companionAppUserId: z.guid()
        .nullish(),
    companionRole: z.object({
        id: z.enum(ApiUserRole),
        name: z.string(),
    })
        .nullish()
        .transform((
            value
        ) => value ?? undefined),
    companionName: z.string(),
    companionPetNames: z.array(z.string())
        .optional(),
    companionAvatarUrl: nullableText,
    messengerUserId: nullableText,
    channel: z.object({
        id: z.enum(ApiChatChannelType),
        name: z.string(),
    }),
    accountName: z.string(),
    lastActivityAt: z.iso.datetime({ offset: true }).nullish(),
    pinnedAt: z.iso.datetime({ offset: true }).nullish(),
    unreadCount: sequence,
    lastMessage: message.nullish()
        .transform((value) => value ?? undefined),
});

/** Проверяет данные изменения переписки от Supabase */
export const chatDialogRealtimeEventSchema: z.ZodType<ChatRealtimeEventResponse> = z.object({
    revision: z.string()
        .regex(/^(0|[1-9]\d*)$/),
    dialogId: z.guid(),
    dialog: dialog.nullable()
        .transform((value) => value ?? undefined),
    message: message.nullable()
        .transform((value) => value ?? undefined),
    removedMessageId: z.guid()
        .nullable(),
    readThroughSequence: sequence,
    counters: z.object({
        unreadMessages: sequence,
        unreadDialogs: sequence,
    }),
})
    .refine((event) => !event.dialog || event.dialog.id === event.dialogId, {
        message: 'Изменение относится к другому диалогу',
    });
